import React, { useContext, useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import { LanguageContext } from '../components/LanguageContext';
import axios from 'axios';

const FormApplications = () => {
  const { language } = useContext(LanguageContext);
  const location = useLocation();
  const [formCategories, setFormCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    submitted_file: null
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  function getCSRFToken() {
    return window.CSRF_TOKEN || '';
  }

  useEffect(() => {
    fetchFormCategories();
  }, []);

  const fetchFormCategories = async () => {
    try {
      const response = await axios.get("/api/get/form-categories", {
        headers: {
          "X-CSRFToken": getCSRFToken(),
        },
        skipInterceptor: true,
      });
      setFormCategories(response.data);
      if (location.state?.selectedCategoryId) {
        const found = response.data.find(c => c.id === location.state.selectedCategoryId);
        if (found) {
          setSelectedCategory(found);
        }
      }
      setLoading(false);

    } catch (error) {
      console.error("Error fetching form categories:", error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      submitted_file: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    const submitData = new FormData();
    submitData.append('category_id', selectedCategory.id);
    submitData.append('applicant_name', formData.applicant_name);
    submitData.append('applicant_email', formData.applicant_email);
    submitData.append('applicant_phone', formData.applicant_phone);
    submitData.append('submitted_file', formData.submitted_file);

    try {
      const response = await axios.post("/api/submit-form", submitData, {
        headers: {
          "X-CSRFToken": getCSRFToken(),
          "Content-Type": "multipart/form-data",
        },
        skipInterceptor: true,
      });

      setMessage({ 
        type: 'success', 
        text: language === 'en' 
          ? 'Application submitted successfully!' 
          : 'درخواست کامیابی سے جمع کرائی گئی!'
      });
      
      // Reset form
      setFormData({
        applicant_name: '',
        applicant_email: '',
        applicant_phone: '',
        submitted_file: null
      });
      setSelectedCategory(null);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || (language === 'en' 
          ? 'Failed to submit application. Please try again.' 
          : 'درخواست جمع کرانے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔')
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-blue-800">
          {language === 'en' ? 'Loading...' : 'لوڈ ہو رہا ہے...'}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 py-12 ${language === 'ur' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-blue-800 mb-8 text-center">
          {language === 'en' ? 'Application Forms' : 'درخواست فارم'}
        </h1>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {!selectedCategory ? (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              {language === 'en' ? 'Select a Category' : 'زمرہ منتخب کریں'}
            </h2>
            
            {formCategories.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">
                  {language === 'en' 
                    ? 'No application forms available at the moment.' 
                    : 'اس وقت کوئی درخواست فارم دستیاب نہیں ہے۔'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {formCategories.map((category) => (
                  <div key={category.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                    <h3 className="text-xl font-semibold text-blue-800 mb-3">
                      {category.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {category.description}
                    </p>
                    {!category.is_enabled && (
                      <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                        <p className="text-red-800 text-sm font-medium">
                          {language === 'en' 
                            ? '⚠️ This form is currently disabled and not accepting submissions.' 
                            : '⚠️ یہ فارم فی الوقت غیر فعال ہے اور درخواستیں قبول نہیں کر رہا۔'}
                        </p>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <a
                        href={category.form_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-center transition-colors"
                      >
                        {language === 'en' ? 'Download Form' : 'فارم ڈاؤن لوڈ کریں'}
                      </a>
                      <button
                        onClick={() => setSelectedCategory(category)}
                        disabled={!category.is_enabled}
                        className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                          category.is_enabled
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {language === 'en' ? 'Apply Now' : 'ابھی درخواست دیں'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
            <div className="mb-6">
              <button
                onClick={() => setSelectedCategory(null)}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {language === 'en' ? 'Back to Categories' : 'زمروں پر واپس جائیں'}
              </button>
            </div>

            <h2 className="text-2xl font-bold text-blue-800 mb-2">
              {selectedCategory.title}
            </h2>
            <p className="text-gray-600 mb-6">
              {selectedCategory.description}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {language === 'en' ? 'Full Name' : 'پورا نام'}
                </label>
                <input
                  type="text"
                  name="applicant_name"
                  value={formData.applicant_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={language === 'en' ? 'Enter your full name' : 'اپنا پورا نام درج کریں'}
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {language === 'en' ? 'Email Address' : 'ای میل ایڈریس'}
                </label>
                <input
                  type="email"
                  name="applicant_email"
                  value={formData.applicant_email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={language === 'en' ? 'Enter your email' : 'اپنا ای میل درج کریں'}
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {language === 'en' ? 'Phone Number' : 'فون نمبر'}
                </label>
                <input
                  type="tel"
                  name="applicant_phone"
                  value={formData.applicant_phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={language === 'en' ? 'Enter your phone number' : 'اپنا فون نمبر درج کریں'}
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {language === 'en' ? 'Upload Filled Form (PDF)' : 'بھرا ہوا فارم اپ لوڈ کریں (PDF)'}
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {language === 'en' 
                    ? 'Please upload the completed form in PDF format' 
                    : 'براہ کرم مکمل شدہ فارم PDF فارمیٹ میں اپ لوڈ کریں'}
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {submitting 
                    ? (language === 'en' ? 'Submitting...' : 'جمع کرایا جا رہا ہے...') 
                    : (language === 'en' ? 'Submit Application' : 'درخواست جمع کرائیں')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormApplications;
